import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {ImplementedAntibodySchemaInterface} from 'antibodies/hooks/useImplementedAntibodySchemaInterfacesQuery';
import {
  NdcColumnInferenceResponse,
  NdcColumnState,
  NdcComponentInputType,
  NdcComponentSequenceType,
  NdcComponentType,
  NdcSpreadsheetInterpretation,
  NdcStartingType,
  isNdcColumnInferenceError,
  isNdcColumnInferenceMissingError,
  isNdcColumnInferenceResponse,
  isNdcColumnInferenceResult,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {assert} from 'Asserts';
import * as ExportUtil from 'util/ExportUtil';
import {initiateTextDownload} from 'util/TextToBlobUtil';

/** Returns the priority of a component type to be used in displaying options and selecting the default
 * type when there is more than one inferred type, where 0 is the highest priority.
 *
 * Returned priority (0 = highest priority):
 * 0: Existing entity name / Registry ID
 * 1: DNA bases
 * 2: AA sequence
 */
export const getPriorityForComponentType = (componentType: NdcComponentType): number => {
  if (componentType.componentInputType === NdcComponentInputType.EXISTING_ENTITY_NAME_OR_REGISTRY_ID) {
    return 0;
  } else if (componentType.componentSequenceType === NdcComponentSequenceType.DNA) {
    return 1;
  } else if (componentType.componentSequenceType === NdcComponentSequenceType.AA) {
    return 2;
  }
  assert(
    false,
    `Invalid component type ${componentType.componentSequenceType} ${componentType.componentInputType}`
  );
};

/** Resolves the effective type for a column, using the selectedType if available, falling back to the
 * highest-priority inferred type. See the docstring on NdcColumnState.selectedType for more details on when
 * selectedType is set.
 */
export function getResolvedColumnType(column: NdcColumnState): NdcComponentType {
  assert(column.inferredTypes.length > 0, 'inferredTypes is supposed to be non-empty');

  return (
    column.selectedType ??
    column.inferredTypes.sort((a, b) => getPriorityForComponentType(a) - getPriorityForComponentType(b))[0]
  );
}

/** Returns true if any column's resolved type is a DNA sequence type. */
export function columnsIncludeDnaType(columns: Array<NdcColumnState>): boolean {
  return columns.some(
    (column) => getResolvedColumnType(column).componentSequenceType === NdcComponentSequenceType.DNA
  );
}

/** Extracts error messages from a column inference response. Returns an array of user-facing error strings
 * for spreadsheet-wide errors, per-column errors, or columns with no inferred types.
 */
export function getColumnInferenceErrorMessages(
  output: NdcColumnInferenceResponse | {errorMessage: string}
): Array<string> {
  if (!isNdcColumnInferenceResponse(output)) {
    return [output.errorMessage];
  }
  const errorMessages: Array<string> = [];
  for (const column of output.columnTypes) {
    if (isNdcColumnInferenceError(column)) {
      errorMessages.push(column.errorMessage);
    } else if (isNdcColumnInferenceMissingError(column)) {
      errorMessages.push(column.errorMessage);
    } else if (isNdcColumnInferenceResult(column) && column.inferredComponentTypes.length === 0) {
      errorMessages.push('An unexpected error occurred while processing your spreadsheet.');
    }
  }
  return errorMessages;
}

export interface CountsOfCreatedComponents {
  dnaDomains: number;
  aaDomains: number;
  dnaChains: number;
  aaChains: number;
  dnaPairs: number;
  aaPairs: number;
  antibodies: number;
  totalEntities: number;
}

function isDnaType(componentType: NdcComponentType): boolean {
  return componentType.componentSequenceType === NdcComponentSequenceType.DNA;
}

function isNewEntity(componentType: NdcComponentType): boolean {
  return componentType.componentInputType === NdcComponentInputType.NEW_SEQUENCE;
}

/** Builds a map from component label (e.g. domain labels) to resolved column type.
 *
 * The resulting lookup only includes the columns of the specified componentFormatType.
 * */
function buildLabelToResolvedColumnTypeLookup(
  columns: Array<NdcColumnState>,
  componentFormatType: 'DOMAIN' | 'CHAIN' | 'VARIABLE_PAIR'
): Map<string, NdcComponentType> {
  const lookup = new Map<string, NdcComponentType>();
  for (const column of columns) {
    const formatKey = column.componentFormatKey;
    if (formatKey.componentFormatType === componentFormatType) {
      lookup.set(formatKey.label, getResolvedColumnType(column));
    }
  }
  return lookup;
}

/** Returns the set of domain labels that have DNA coverage. */
function buildDnaDomainsFromLookup(domainTypeLookup: Map<string, NdcComponentType>): Set<string> {
  const dnaDomains = new Set<string>();
  for (const [key, colType] of domainTypeLookup) {
    if (isDnaType(colType)) {
      dnaDomains.add(key);
    }
  }
  return dnaDomains;
}

function sumEntityCounts(counts: Omit<CountsOfCreatedComponents, 'totalEntities'>): number {
  return (
    counts.dnaDomains +
    counts.aaDomains +
    counts.dnaChains +
    counts.aaChains +
    counts.dnaPairs +
    counts.aaPairs +
    counts.antibodies
  );
}

/** Counts domains from domain-level type information. Meant to be used with DOMAINS or PAIRS starting types.
 *
 * Domain creation rules per column resolved type:
 * - NEW_SEQUENCE + DNA: creates DNA domain + AA domain (via translation)
 * - NEW_SEQUENCE + AA: creates AA domain only
 * - EXISTING + DNA: creates AA domain (via translation), DNA domain already exists
 * - EXISTING + AA: nothing created
 *
 * Domains whose label is not in the lookup are skipped. This happens in PAIRS starting type where variable
 * domains are covered by pair columns rather than individual domain columns.
 *
 * @param domainTypeLookup - A map from domain label to the resolved column type.
 */
function countDomains(
  antibodyFormat: AntibodyFormat,
  domainTypeLookup: Map<string, NdcComponentType>
): {dnaDomains: number; aaDomains: number} {
  let dnaDomains = 0;
  let aaDomains = 0;

  for (const domain of antibodyFormat.distinctDomains) {
    const colType = domainTypeLookup.get(domain.label);
    if (colType == null) {
      continue;
    }

    if (isNewEntity(colType) && isDnaType(colType)) {
      dnaDomains++;
      aaDomains++;
    } else if (isNewEntity(colType)) {
      aaDomains++;
    } else if (isDnaType(colType)) {
      // When referencing an existing DNA domain, the backend translates it to create an AA domain. If an AA
      // translation already exists, then it will be merged into the existing AA domain.
      aaDomains++;
    }
  }

  return {dnaDomains, aaDomains};
}

/** Counts chains from domain coverage. Meant to be used with DOMAINS or PAIRS starting types.
 *
 * DNA chains are only counted/created when all constituent domains have DNA coverage.
 *
 * E.g. for a FAB with Heavy Chain having VH and CH1 domains, if the VH domain is DNA but the CH1 domain is
 * AA, then no DNA chain will be created. Only an AA chain will be created.
 *
 * @param availableDomains - The set of domain labels that are available for assembly.
 * @param dnaDomains - The set of domain labels that have DNA coverage.
 */
function countChains(
  antibodyFormat: AntibodyFormat,
  dnaDomains: Set<string>
): {dnaChains: number; aaChains: number} {
  let dnaChains = 0;
  let aaChains = 0;

  for (const chain of antibodyFormat.distinctChains) {
    aaChains++;
    if (chain.domains.every((d) => dnaDomains.has(d.label))) {
      dnaChains++;
    }
  }

  return {dnaChains, aaChains};
}

/** Counts pairs from domain coverage. Meant to be used with DOMAINS or CHAINS starting types.
 *
 * DNA pairs are only created when both constituent domains have DNA coverage.
 */
function countPairs(
  antibodyFormat: AntibodyFormat,
  dnaDomains: Set<string>
): {dnaPairs: number; aaPairs: number} {
  let dnaPairs = 0;
  let aaPairs = 0;

  for (const pair of antibodyFormat.distinctVariablePairs) {
    aaPairs++;

    if (dnaDomains.has(pair.vhVaVg.label) && dnaDomains.has(pair.vlVbVd.label)) {
      dnaPairs++;
    }
  }

  return {dnaPairs, aaPairs};
}

/** For DOMAINS starting type, columns map to individual domains. Chains and pairs are derived from
 * domain-level type information.
 */
function getCountsForDomainsStartingType(
  spreadsheetInterpretation: NdcSpreadsheetInterpretation,
  antibodyFormat: AntibodyFormat
): CountsOfCreatedComponents {
  const {rowCount} = spreadsheetInterpretation;
  const domainTypeLookup = buildLabelToResolvedColumnTypeLookup(spreadsheetInterpretation.columns, 'DOMAIN');
  const {dnaDomains, aaDomains} = countDomains(antibodyFormat, domainTypeLookup);
  const dnaDomainLabels = buildDnaDomainsFromLookup(domainTypeLookup);
  const {dnaChains, aaChains} = countChains(antibodyFormat, dnaDomainLabels);
  const {dnaPairs, aaPairs} = countPairs(antibodyFormat, dnaDomainLabels);
  const counts = {
    dnaDomains: dnaDomains * rowCount,
    aaDomains: aaDomains * rowCount,
    dnaChains: dnaChains * rowCount,
    aaChains: aaChains * rowCount,
    dnaPairs: dnaPairs * rowCount,
    aaPairs: aaPairs * rowCount,
    antibodies: rowCount,
  };
  return {...counts, totalEntities: sumEntityCounts(counts)};
}

/** For CHAINS starting type, columns map to distinct chains.
 *
 * The chain's resolved type propagates to all constituent domains:
 * - NEW + DNA chain: creates DNA chain, AA chain, all domains as DNA + AA
 * - NEW + AA chain: creates AA chain, all domains as AA only
 * - EXISTING + DNA chain: creates AA chain (translated), AA domains (from decomposition)
 * - EXISTING + AA chain: no entities created
 */
function getCountsForChainsStartingType(
  spreadsheetInterpretation: NdcSpreadsheetInterpretation,
  antibodyFormat: AntibodyFormat
): CountsOfCreatedComponents {
  const {rowCount} = spreadsheetInterpretation;
  const chainTypeLookup = buildLabelToResolvedColumnTypeLookup(spreadsheetInterpretation.columns, 'CHAIN');

  let dnaChains = 0;
  let aaChains = 0;
  /** Domain labels that have DNA coverage. */
  const dnaDomainLabels = new Set<string>();
  /** Domain labels that are from a new chain. This is used here particularly to avoid counting domains that
   * are from an existing chain, which decompose to existing domains. */
  const domainIsFromNewChain = new Set<string>();

  for (const chain of antibodyFormat.distinctChains) {
    const colType = chainTypeLookup.get(chain.label);
    assert(
      colType != null,
      `When starting with CHAINS, expected chain "${chain.label}" to be present in the lookup`
    );

    const chainIsDna = isDnaType(colType);
    const chainIsNew = isNewEntity(colType);

    if (chainIsNew && chainIsDna) {
      dnaChains++;
      aaChains++;
    } else if (chainIsNew) {
      aaChains++;
    } else if (chainIsDna) {
      aaChains++;
    }

    for (const domain of chain.domains) {
      if (chainIsNew) {
        domainIsFromNewChain.add(domain.label);
      }
      if (chainIsDna) {
        dnaDomainLabels.add(domain.label);
      }
    }
  }

  let dnaDomains = 0;
  let aaDomains = 0;
  for (const domain of antibodyFormat.distinctDomains) {
    const isNew = domainIsFromNewChain.has(domain.label);
    const isDna = dnaDomainLabels.has(domain.label);
    if (isNew && isDna) {
      dnaDomains++;
      aaDomains++;
    } else if (isNew || isDna) {
      // When referencing an existing DNA domain, the backend translates it to create an AA domain. If an AA
      // translation already exists, then it will be merged into the existing AA domain.
      aaDomains++;
    }
  }

  const {dnaPairs, aaPairs} = countPairs(antibodyFormat, dnaDomainLabels);

  const counts = {
    dnaDomains: dnaDomains * rowCount,
    aaDomains: aaDomains * rowCount,
    dnaChains: dnaChains * rowCount,
    aaChains: aaChains * rowCount,
    dnaPairs: dnaPairs * rowCount,
    aaPairs: aaPairs * rowCount,
    antibodies: rowCount,
  };
  return {...counts, totalEntities: sumEntityCounts(counts)};
}

/** For PAIRS starting type, the spreadsheet has pair columns (for variable domain pairs) and non-paired
 * domain columns (for domains not covered by any pair, typically constant domains).
 *
 * Pair counting: pairs are always existing entities (they reference other sequences, but are not sequences
 * themselves). A DNA pair creates an AA pair (via its domains being translated and then combining into an AA
 * pair). An AA pair creates nothing.
 *
 * Domain counting: non-paired domains are counted from their domain columns (same rules as DOMAINS starting
 * type). Domains covered by pair columns get DNA coverage from the pair column type.
 *
 * Chain counting: same as DOMAINS — DNA chains require ALL constituent domains to have DNA coverage.
 */
function getCountsForPairsStartingType(
  spreadsheetInterpretation: NdcSpreadsheetInterpretation,
  antibodyFormat: AntibodyFormat
): CountsOfCreatedComponents {
  const {rowCount} = spreadsheetInterpretation;
  const domainTypeLookup = buildLabelToResolvedColumnTypeLookup(spreadsheetInterpretation.columns, 'DOMAIN');
  const {dnaDomains, aaDomains} = countDomains(antibodyFormat, domainTypeLookup);
  const dnaDomainLabels = buildDnaDomainsFromLookup(domainTypeLookup);
  const pairTypeLookup = buildLabelToResolvedColumnTypeLookup(
    spreadsheetInterpretation.columns,
    'VARIABLE_PAIR'
  );

  let aaPairs = 0;
  let aaDomainFromPairs = 0;
  for (const pair of antibodyFormat.distinctVariablePairs) {
    const colType = pairTypeLookup.get(pair.label);
    assert(
      colType != null,
      `When starting with PAIRS, expected pair "${pair.label}" to be present in the lookup`
    );

    // Add DNA coverage from pairs
    if (isDnaType(colType)) {
      dnaDomainLabels.add(pair.vhVaVg.label);
      dnaDomainLabels.add(pair.vlVbVd.label);
      // DNA pair -> AA pair created, and constituent domains are translated into AA domains
      aaPairs++;
      aaDomainFromPairs += 2;
    }
  }

  const {dnaChains, aaChains} = countChains(antibodyFormat, dnaDomainLabels);
  const counts = {
    dnaDomains: dnaDomains * rowCount,
    aaDomains: (aaDomains + aaDomainFromPairs) * rowCount,
    dnaChains: dnaChains * rowCount,
    aaChains: aaChains * rowCount,
    dnaPairs: 0,
    aaPairs: aaPairs * rowCount,
    antibodies: rowCount,
  };
  return {...counts, totalEntities: sumEntityCounts(counts)};
}

/** Calculates the expected counts of entities that will be created during NDC spreadsheet import.
 *
 * Assumes that the spreadsheet interpretation is valid (e.g. all required columns are present).
 *
 * For DOMAINS starting type: columns map to individual domains. Chains and pairs are derived.
 * For CHAINS starting type: columns map to chains. Domains are decomposed from chains. Pairs are derived.
 * For PAIRS starting type: pair columns cover variable domains and determine pair creation. Non-paired domain
 * columns typically cover constant domains. DNA chains are derived from DNA domain coverage.
 */
export function getCountsOfCreatedComponents(
  spreadsheetInterpretation: NdcSpreadsheetInterpretation,
  selectedStartingType: NdcStartingType,
  antibodyFormat: AntibodyFormat
): CountsOfCreatedComponents {
  switch (selectedStartingType) {
    case NdcStartingType.DOMAINS:
      return getCountsForDomainsStartingType(spreadsheetInterpretation, antibodyFormat);

    case NdcStartingType.CHAINS:
      return getCountsForChainsStartingType(spreadsheetInterpretation, antibodyFormat);

    case NdcStartingType.PAIRS:
      return getCountsForPairsStartingType(spreadsheetInterpretation, antibodyFormat);

    default:
      assert(false, `Unexpected starting type: ${selectedStartingType}`);
  }
}

export interface AntibodySchemaFormatMaps {
  schemaIdToFormat: Map<string, AntibodyFormat>;
  formatIdToSchemaIds: Map<string, Set<string>>;
  allSchemaIds: Set<string>;
}

function buildSchemaIdToFormatMap(
  implementedInterfaces: Array<ImplementedAntibodySchemaInterface>
): Map<string, AntibodyFormat> {
  return new Map(
    implementedInterfaces.map((implementedInterface) => [
      implementedInterface.schema.id,
      implementedInterface.format,
    ])
  );
}

function buildFormatIdToSchemaIdsMap(
  implementedInterfaces: Array<ImplementedAntibodySchemaInterface>
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const implementedInterface of implementedInterfaces) {
    const formatId = implementedInterface.format.id;
    if (!map.has(formatId)) {
      map.set(formatId, new Set());
    }
    map.get(formatId)!.add(implementedInterface.schema.id);
  }
  return map;
}

function buildAllSchemaIdsSet(implementedInterfaces: Array<ImplementedAntibodySchemaInterface>): Set<string> {
  return new Set(implementedInterfaces.map((implementedInterface) => implementedInterface.schema.id));
}

/**
 * Builds maps and sets for navigating relationships between antibody schemas and formats from the implemented
 * antibody schema interfaces.
 */
export function buildAntibodySchemaFormatMaps(
  implementedAntibodySchemaInterfaces: Array<ImplementedAntibodySchemaInterface>
): AntibodySchemaFormatMaps {
  return {
    schemaIdToFormat: buildSchemaIdToFormatMap(implementedAntibodySchemaInterfaces),
    formatIdToSchemaIds: buildFormatIdToSchemaIdsMap(implementedAntibodySchemaInterfaces),
    allSchemaIds: buildAllSchemaIdsSet(implementedAntibodySchemaInterfaces),
  };
}

/**
 * Generates spreadsheet template column headers based on the antibody format and starting type.
 *
 * Keep in sync with build_column_name_to_component_key in benchling/antibodies/nested_data_creation/spreadsheet_import/domain/infer_columns.py
 * Exported for testing.
 */
export function generateSpreadsheetTemplateHeaders(
  format: AntibodyFormat,
  startingType: NdcStartingType
): Array<string> {
  switch (startingType) {
    case NdcStartingType.DOMAINS:
      return format.distinctDomains.map((domain) => domain.label);
    case NdcStartingType.CHAINS:
      return format.distinctChains.map((chain) => chain.label);
    case NdcStartingType.PAIRS: {
      // Headers match build_column_name_to_component_key_for_pairs in infer_columns.py (pairs, then unpaired domains).
      const pairHeaders = format.distinctVariablePairs.map((pair) => pair.label);

      const pairedDomainRefs = format.distinctVariablePairs.flatMap((pair) => [
        ...pair.vhVaVg.refs,
        ...pair.vlVbVd.refs,
      ]);
      const nonPairedDomainHeaders = format.distinctDomains
        .filter((domain) => !domain.refs.some((ref) => pairedDomainRefs.includes(ref)))
        .map((domain) => domain.label);

      return [...pairHeaders, ...nonPairedDomainHeaders];
    }
    default:
      throw new Error(`Unknown starting type: ${startingType}`);
  }
}

/**
 * Download a spreadsheet template with column headers based on the antibody format and starting type.
 *
 * The backend expects only component columns (domains or chains) in the spreadsheet.
 * Each row in the spreadsheet represents one antibody to be created.
 */
export function downloadSpreadsheetTemplate(format: AntibodyFormat, startingType: NdcStartingType): void {
  const headers = generateSpreadsheetTemplateHeaders(format, startingType);
  const csvContent = ExportUtil.formatRows([headers], ',');
  const startingTypeLabel = startingType.toLowerCase().replace(/ /g, '-');
  const sanitizedFormatName = format.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
  const filename = `protein-import-template-${sanitizedFormatName}-${startingTypeLabel}.csv`;

  initiateTextDownload(csvContent, filename);
}
